import { AxiosInstance } from 'axios';
import { TokenStorage } from 'shared/lib/token-storage';
import { apiInstance } from './config';
import { axiosInstance } from './axios-instance';

export interface CheckOldPasswordRequest {
  old_password: string;
}

export interface ChangePasswordRequest {
  new_password1: string;
  new_password2: string;
}

export interface AuthResponse {
  user_id: string;
  access_token?: string;
  refresh_token?: string;
  is_active: boolean;
  is_verified: boolean;
  access:string;
  refresh:string;
}

export interface LoginRequest {
  email: string;
  password: string;
  remember_me:boolean;
}

export interface RegisterRequest {
  email: string;
  first_name: string;
  last_name: string;
  password: string;
  phone_number?: string;
  offer_agreement?: boolean;
}

export interface AuthError {
  detail: string;
  code?: string;
  status_code?: number;
}

export interface ResetPasswordRequest {
  new_password1: string;
  new_password2: string;
  uid: string;
  token: string;
}

export class AuthApi {
  private api: AxiosInstance;

  constructor(api: AxiosInstance = apiInstance) {
    this.api = api;
  }

  async login(data: LoginRequest): Promise<AuthResponse> {
    try {
      const response = await this.api.post<AuthResponse>('/api/auth/login/', data);
      return response.data;
    } catch (error: any) {
      if (error.response) {
        throw {
          detail: error.response.data.detail || 'Помилка авторизації',
          code: error.response.data.code,
          status_code: error.response.status
        } as AuthError;
      }
      throw { detail: 'Помилка мережі' } as AuthError;
    }
  }

  async refreshToken(): Promise<AuthResponse> {
    try {
      const refreshToken = TokenStorage.getRefreshToken();
      if (!refreshToken) {
        throw new Error('No refresh token available');
      }


      const response = await this.api.post<AuthResponse>('/api/auth/token/refresh/', {
        refresh: refreshToken
      });

      return response.data;
    } catch (error: any) {
      console.error('refreshToken: Error during refresh token API call:', error);
      if (error.response) {
        throw {
          detail: error.response.data.detail || 'Помилка оновлення токену',
          code: error.response.data.code,
          status_code: error.response.status
        } as AuthError;
      }
      throw { detail: 'Помилка мережі' } as AuthError;
    }
  }

  // Метод для реєстрації нового користувача
  async register(data: RegisterRequest): Promise<AuthResponse> {
    try {
      const response = await this.api.post<AuthResponse>('/api/auth/register/', data);
      return response.data;
    } catch (error: any) {
      if (error.response) {
        throw {
          detail: error.response.data.detail || 'Помилка реєстрації',
          code: error.response.data.code,
          status_code: error.response.status
        } as AuthError;
      }
      throw { detail: 'Помилка мережі' } as AuthError;
    }
  }

  async logout(): Promise<void> {
    try {
      await axiosInstance.post('/api/auth/logout/');
    } catch (error) {
      console.error('Помилка при виході з системи:', error);
    }
  }

  async requestPasswordReset(email: string): Promise<void> {
    try {
      await this.api.post('/api/auth/password/reset/', { email });
    } catch (error: any) {
      if (error.response) {
        throw {
          detail: error.response.data.detail || 'Ошибка при запросе восстановления пароля',
          code: error.response.data.code,
          status_code: error.response.status
        } as AuthError;
      }
      throw { detail: 'Ошибка сети' } as AuthError;
    }
  }

  async resetPassword(data: ResetPasswordRequest): Promise<void> {
    try {
      await this.api.post('/api/auth/password/reset/confirm/', data);
    } catch (error: any) {
      if (error.response) {
        throw {
          detail: error.response.data.detail || 'Ошибка при сбросе пароля',
          code: error.response.data.code,
          status_code: error.response.status
        } as AuthError;
      }
      throw { detail: 'Ошибка сети' } as AuthError;
    }
  }

  async checkOldPassword(data: CheckOldPasswordRequest): Promise<void> {
    try {
      await axiosInstance.post('/api/auth/password/check/', data);
    } catch (error: any) {
      if (error.response) {
        throw {
          status_code: error.response.status,
          detail: error.response.data.detail || 'Помилка перевірки паролю'
        };
      }
      throw { detail: 'Помилка мережі' };
    }
  }

  async changePassword(data: ChangePasswordRequest): Promise<void> {
    try {
      await axiosInstance.put('/api/auth/password/change/', data);
    } catch (error: any) {
      if (error.response) {
        throw {
          status_code: error.response.status,
          detail: error.response.data.detail || 'Помилка зміни паролю'
        };
      }
      throw { detail: 'Помилка мережі' };
    }
  }
}

export const authApi = new AuthApi();